<template>
  <div
    :id="id"
    class="interactive-canvas-wrapper primary-canvas-wrapper escalon-absolute-cover"
    :class="cursorClass"
    :style="{ width, height }"
    ref="wrapper"
    tabindex="0"
    @keydown.self.delete.prevent="handleDelete"
    @keypress.self.prevent="handleKeypress"
    @mousedown.self.left="handleMousedown"
    @mousemove.self="handleMousemove"
    @mouseup.self.left="handleMouseup"
    @mouseout.self="handleMouseout"
    v-hammer:press="handlePanstart"
    v-hammer:panstart="handlePanstart"
    v-hammer:pan="handlePanmove"
    v-hammer:panend="handlePanend"
    v-hammer:tap="handleTap">
    <image-not-found v-if="imgNotFound" />
    <pixel-shaded-canvas
      v-if="imgAvailable"
      class="main-image"
      :img="imgObject"
      :colorSettings="colorSettings"
      :canvasSize="viewFit"
      :imgSize="imgSize"
      :transform="transform"
      :isFlipped="isFlipped"
      :isFlipped90="isFlipped90"
      :isSideBySide="isSideBySide"
      :isDeepDeeAnnotation="isDeepDeeAnnotation"
      :style="{
        'border-color': borderColor,
        'outline-color': borderColor
      }"
      :hires="true"/>
    <div v-if="!isMask">
      <canvas-annotation
        v-for="(annotation, index) in annotations"
        :key="index"
        :annotation="annotation"
        :canvasSize="viewFit"
        :imgSize="imgSize"
        :transform="transform"
        :isFlipped="isFlipped"
        :isFlipped90="isFlipped90"/>
      <canvas-annotation
        v-if="inMagnifyMode"
        :canvasSize="viewSize"
        :imgSize="imgSize"
        :annotation="magnifier"/>
      <canvas-annotation
        v-if="isDeepDeeAnnotation"
        :annotation="deepDeeAnnotation"
        :canvasSize="viewFit"
        :imgSize="imgSize"
        :transform="transform"
        :isFlipped="isFlipped"
        :isFlipped90="isFlipped90"/>
    </div>
    <v-progress-circular
        v-if="imgIsLoading"
        class="escalon-absolute-center"
        indeterminate/>
  </div>
</template>

<script>
import pixelShadedCanvas from "./PixelShadedCanvas";
import canvasAnnotation from "./CanvasAnnotation";
import touchMouseEvents from "../../components/mixins/TouchMouseEvents";
import imageNotFound from "../../components/misc/ImageNotFound";
import { setSize, setTransform } from "../geometry";
import Magnifier from "../annotations/magnifier";
// import { logDetailed } from "../../helpers/utils";

export default {
  name: "annotateableCanvas",
  components: { pixelShadedCanvas, canvasAnnotation, imageNotFound },
  mixins: [touchMouseEvents],
  props: {
    id: {
      type: String,
      default: "primary-canvas-wrapper"
    },
    borderColor: {
      type: String,
      default: "white"
    },
    context: Object,
    imgIsLoading: Boolean,
    imgAvailable: Boolean,
    imgNotFound: Boolean,
    imgObject: [HTMLImageElement, HTMLCanvasElement],
    imgSize: Object,
    imgOffset: Object,
    colorSettings: Object,
    viewSize: Object,
    viewFit: Object,
    transform: Object,
    isFlipped: Boolean,
    isFlipped90: Boolean,
    annotations: Array,
    activeAnnotation: Object,
    realLocation: Function,
    isInBounds: Function,
    annotationByPosition: Function,
    inMagnifyMode: Boolean,
    inAddMode: Boolean,
    inEditMode: Boolean,
    inShapeFillMode: Boolean,
    isMagnifying: Boolean,
    isDragLock: Boolean,
    magnifier: Magnifier,
    isSideBySide: Boolean,
    isMask: Boolean,
    isDeepDeeAnnotation: Boolean,
    deepDeeAnnotation: Object
  },
  data() {
    return {
      cursorClass: "",
      dragStart: null,
      dragStartCenter: null,
      isMousedown: false,
      disableCustomEvents: false
    };
  },
  computed: {
    w() {
      return (this.viewSize && this.viewSize.w) || 0;
    },
    width() {
      return this.w + "px";
    },
    h() {
      return (this.viewSize && this.viewSize.h) || 0;
    },
    height() {
      return this.h + "px";
    }
  },
  methods: {
    // top-level interaction methods
    handleInteractionStart(e) {
      const { x, y } = this.realLocation(e.offsetX, e.offsetY);
      // logDetailed("AnnotateableCanvas", "handleInteractionStart", 
      //   "position", { x, y });
      this.startFocus();
      if (!this.isInBounds(x, y)) {
        this.setDisableCustomEvents(true);
        this.setMouseup();
      } else if (this.inEditMode && this.annotations.length == 0 && this.transform.zoom <= this.transform.fitZoom) {
        this.setDisableCustomEvents(true);
        this.setMouseup();
      } else if (this.inMagnifyMode) {
        this.setDisableCustomEvents(false);
        this.updateMagnifier(e.offsetX, e.offsetY, x, y);
      } else if ((!this.isFlipped && !this.isFlipped90) || (this.isDragLock && (this.inAddMode || this.activeAnnotation))) {
        this.setDisableCustomEvents(false);
        let editingAddedAnnotation = this.inAddMode && this.activeAnnotation;
        if (!editingAddedAnnotation)
          this.$emit("mousedownAnnotation", { x, y, e });
      } else if (this.isDragLock && !this.activeAnnotation || (!this.activeAnnotation && this.transform.zoom > this.transform.fitZoom)) {
        this.setDisableCustomEvents(false);
        this.initiateDragStart(x, y);
      } 
    },
    handleInteractionMove(e) {
      const { x, y } = this.realLocation(e.offsetX, e.offsetY);
      // logDetailed("AnnotateableCanvas", "handleInteractionMove", 
      //   "position", { x, y });
      if (!this.isInBounds(x, y)) this.setMouseup();
      else if (this.isDragLock && !this.activeAnnotation || (!this.activeAnnotation && this.transform.zoom > this.transform.fitZoom)) this.updateDrag(x, y);
      else if (((!this.isFlipped && !this.isFlipped90) && this.activeAnnotation) || (this.isDragLock && this.activeAnnotation))
        this.updateActive(x, y);
    },
    handleHover(e) {
      this.cursorClass = "";
      const { x, y } = this.realLocation(e.offsetX, e.offsetY);
      if (this.inMagnifyMode) this.updateMagnifier(e.offsetX, e.offsetY, x, y);
      else if (!this.isFlipped && !this.isFlipped90) this.checkHover(x, y);
    },
    handleMouseout() 
    {
      if (!this.activeAnnotation) this.stopFocus();
      if (this.dragStart) this.clearDragStart();
      if (this.inMagnifyMode) this.magnifierMouseup();
    },
    handleInteractionEnd() {
      if (!this.activeAnnotation) this.stopFocus();
      if (this.dragStart) this.clearDragStart();
      if (this.inMagnifyMode) this.magnifierMouseup();
      else this.$emit("mouseupAnnotation");
    },
    handleDelete() {
      if (this.activeAnnotation) 
        this.$emit("deleteActiveAnnotation");
    },

    // annotation methods
    updateActive(x, y) {
      this.$emit("mousemoveAnnotation", { x, y });
      this.cursorClass = this.activeAnnotation.getActiveClass(x, y);
    },
    checkHover(x, y) {
      const hoveredAnnotation = this.annotationByPosition({ x, y });
      if (hoveredAnnotation && (!this.isDragLock || this.activeAnnotation == hoveredAnnotation)) 
        this.cursorClass = hoveredAnnotation.getActiveClass(x, y);
    },

    // misc methods
    startFocus() {
      this.$refs.wrapper.focus();
    },
    stopFocus() {
      this.$refs.wrapper.blur();
    },
    setDisableCustomEvents(val) {
      this.disableCustomEvents = val;
    },

    // drag methods
    initiateDragStart(x, y) {
      this.dragStart = { x, y };
      this.dragStartCenter = {
        x: this.transform.centerX,
        y: this.transform.centerY
      };
      // logDetailed(
      //   "AnnotateableCanvas",
      //   "initiateDragStart",
      //   "dragStart",
      //   this.dragStart
      // );
    },
    updateDrag(x, y) {
      if (this.isMousedown)
        if (!this.dragStart) this.initiateDragStart(x, y);
        else this.dragTo(x, y);
      else this.clearDragStart();
    },
    dragTo(x, y) {
      let dx = this.dragStart.x - x;
      let dy = this.dragStart.y - y;
      // logDetailed("AnnotateableCanvas", "dragTo", "x", x, "y", y);
      this.$emit("panBy", { dx, dy });
    },
    clearDragStart() {
      this.dragStart = null;
      this.dragStartCenter = null;
    },

    // magnify methods
    instantiateMagnifier() {
      setTransform(this.magnifier.transform, this.transform);
      setSize(this.magnifier.imgSize, this.imgSize);
      setSize(this.magnifier.viewSize, this.viewSize);
      setSize(this.magnifier.fit, this.viewFit);
    },
    updateMagnifier(offsetX, offsetY, x, y) {
      if (!this.imgOffset) return;
      this.$emit("updateMagnifier", {
        offsetX: offsetX - this.imgOffset.x,
        offsetY: offsetY - this.imgOffset.y,
        x,
        y
      });

      if (this.isMousedown || this.isMagnifying) this.magnifierMousedown();
      else this.magnifierMouseup();
    },
    magnifierMouseup() {
      this.$emit("mouseupMagnifier");
    },
    magnifierMousedown() {
      this.$emit("mousedownMagnifier");
    },
    setupAnnotationsAndMagnifier() {
      if (this.context) {
        this.context.setInitialAnnotationSizes();
        this.instantiateMagnifier();
      }
    }
  },
  mounted() {
    this.setupAnnotationsAndMagnifier();
  },
  watch: {
    transform: {
      handler() {
        if (this.magnifier && this.transform)
          setTransform(this.magnifier.transform, this.transform);
      },
      deep: true
    },
    imgSize: {
      handler() {
        if (this.magnifier && this.imgSize)
          setSize(this.magnifier.imgSize, this.imgSize);
      },
      deep: true
    },
    viewSize: {
      handler() {
        if (this.magnifier && this.viewSize)
          setSize(this.magnifier.viewSize, this.viewSize);
      },
      deep: true
    },
    viewFit: {
      handler() {
        if (this.magnifier && this.viewFit)
          setSize(this.magnifier.fit, this.viewFit);
      },
      deep: true
    },
    context() {
      this.setupAnnotationsAndMagnifier();
    },
    disableCustomEvents() {
      if (this.disableCustomEvents)
        this.$refs.wrapper.style.touchAction = "auto";
      else this.$refs.wrapper.style.touchAction = "none";
    },
    isMagnifying() {
      if (this.isMagnifying) this.magnifierMousedown();
      else this.magnifierMouseup();
    }
  }
};
</script>